<template>
    <div class="pay-in__body px-4 py-3 d-flex flex-column">

        <!-- Created payments list -->
        <created-partial-payment-list
            v-if="createdPayments.length && !showPaymentDetailsForm"
            :payments="createdPayments"
            :showAddPayment="true"
            :disableAdd="remainingAmountDue <= 0"
            class="mb-3"
            @remove="removeCreatedPayment"
            @edit="editCreatedPayment"
            @add="$emit('update:showPaymentDetailsForm', true)"
        />

        <!-- Payment details form -->
        <template v-if="showPaymentDetailsForm">
            <!-- Reservation Items table -->
            <div class="mb-1">
                <label class="mb-1 font-weight-600 darkest">Reservation Items</label>

                <div class="pay-in__tables-wrapper">
                <!-- Pax -->
                <template v-if="paxItems.length">
                    <table class="w-100 dark mb-3 pay-in__items-table">
                        <colgroup>
                            <col class="col-description" /><col class="col-seller" /><col class="col-qty" /><col class="col-price" /><col class="col-qtyadd" /><col class="col-total" />
                        </colgroup>
                         
                        <tr class="dark border-bottom-light">
                            <th class="py-0">Ticket Type</th>
                            <th class="py-0">Seller</th>
                            <th class="py-0 text-center">Qty</th>
                            <th class="py-0 text-center">Price/unit</th>
                            <th class="py-0 text-center">Qty. to add</th>
                            <th class="py-0 text-right">Total</th>
                        </tr>
                        <tr
                            v-for="(item, index) in paxItems"
                            :key="'pax-' + index"
                            class="border-bottom-light"
                        >
                            <td class="darker py-0">{{ item.audienceTypeDescription }}</td>
                            <td class="py-0">{{ item.sellerName }}</td>
                            <td class="text-center py-0">{{ item.remainingQty }}</td>
                            <td class="text-center py-0">{{ formatPrice(getItemPrice(item)) }} €</td>
                            <td class="text-center py-0">
                                <div class="pay-in__qty-control">
                                    <span class="border-light radius-normal mx-1 text-center pointer pay-in__qty-btn" @click="decrementItemQuantity(item)"><i class="ico ico-minus dark m-0 pay-in__qty-icon"></i></span>
                                    <input type="number" placeholder="0" :min="item.minQty || 0" class="text-center px-0 py-0 bg-lighter darker pay-in__qty-input" v-model.number="item.qtyToAdd" @change="keepSelectedQuantityInBounds(item)" />
                                    <span class="border-light radius-normal mx-1 text-center pointer pay-in__qty-btn" @click="incrementItemQuantity(item)"><i class="ico ico-add dark m-0 pay-in__qty-icon"></i></span>
                                </div>
                            </td>
                            <td class="text-right py-0">{{ formatPrice(item.qtyToAdd * getItemPrice(item)) }} €</td>
                        </tr>
                    </table>
                </template>

                <!-- Extras -->
                <template v-if="extraItems.length">
                    <table class="w-100 dark mb-3 pay-in__items-table">
                        <colgroup>
                            <col class="col-description" /><col class="col-seller" /><col class="col-qty" /><col class="col-price" /><col class="col-qtyadd" /><col class="col-total" />
                        </colgroup>
                        <tr>
                            <th class="py-0 darker" colspan="6">Extras</th>
                        </tr>
                        <tr class="dark border-bottom-light">
                            <th class="py-0">Description</th>
                            <th class="py-0">Seller</th>
                            <th class="py-0 text-center">Qty</th>
                            <th class="py-0 text-center">Price/unit</th>
                            <th class="py-0 text-center">Qty. to add</th>
                            <th class="py-0 text-right">Total</th>
                        </tr>
                        <tr
                            v-for="(item, index) in extraItems"
                            :key="'extra-' + index"
                            class="border-bottom-light"
                        >
                            <td class="darker py-0">{{ item.component.name }} - {{ item.componentPrice.name }}</td>
                            <td class="py-0">{{ item.sellerName }}</td>
                            <td class="text-center py-0">{{ item.remainingQty }}</td>
                            <td class="text-center py-0">{{ formatPrice(getItemPrice(item.componentPrice)) }} €</td>
                            <td class="text-center py-0">
                                <div class="pay-in__qty-control">
                                    <span class="border-light radius-normal mx-1 text-center pointer pay-in__qty-btn" @click="decrementItemQuantity(item)"><i class="ico ico-minus dark m-0 pay-in__qty-icon"></i></span>
                                    <input type="number" placeholder="0" min="0" class="text-center px-0 py-0 bg-lighter darker pay-in__qty-input" v-model.number="item.qtyToAdd" @change="keepSelectedQuantityInBounds(item)" />
                                    <span class="border-light radius-normal mx-1 text-center pointer pay-in__qty-btn" @click="incrementItemQuantity(item)"><i class="ico ico-add dark m-0 pay-in__qty-icon"></i></span>
                                </div>
                            </td>
                            <td class="text-right py-0">{{ formatPrice(item.qtyToAdd * getItemPrice(item.componentPrice)) }} €</td>
                        </tr>
                    </table>
                </template>

                </div><!-- /.pay-in__tables-wrapper -->
                <div class="text-right fs-small dark pay-in__tables-total">Total: {{ formatPrice(itemsTotal) }} €</div>
            </div>

            <hr class="pay-in__separator mb-2" />

            <payment-method-form
                :payments="paymentFormEntries"
                :isAmountCovered="isItemsTotalFullyPaid"
                :remainingToPay="itemsTotal"
                :allowEmpty="!!editPayment"
            />

            <hr class="pay-in__separator mb-2" />

            <div>
                <label class="ml-2 d-flex align-items-center">
                    <span>Payment ID <span class="pay-in__required">*</span></span>
                    <i
                        class="fa fa-info-circle fs-normal dark has-tooltip ml-2"
                        v-tooltip="{
                            content: 'A custom identifier for easy payment recognition.',
                            html: true
                        }"
                    ></i>
                </label>
                <div class="pay-in__notes-wrapper">
                    <input
                        type="text"
                        class="pay-in__notes-input"
                        v-model.trim="form.reference"
                        :maxlength="REFERENCE_CHARACTER_LIMIT"
                    />
                    <div class="pay-in__notes-counter">
                        {{ (form.reference && form.reference.length) || 0 }}/{{ REFERENCE_CHARACTER_LIMIT }}
                    </div>
                </div>
            </div>

            <!-- Notes -->
            <div class="mb-1">
                <label class="ml-2">Notes</label>
                <div class="pay-in__notes-wrapper">
                    <input
                        type="text"
                        class="pay-in__notes-input"
                        v-model="form.notes"
                        :maxlength="NOTES_CHARACTER_LIMIT"
                    />
                    <div class="pay-in__notes-counter">
                        {{ form.notes.length || 0 }}/{{ NOTES_CHARACTER_LIMIT }}
                    </div>
                </div>
            </div>

            <hr class="pay-in__separator mb-3" />

          <!-- Checkboxes -->
            <div class="mb-2">
                <div v-if="isItemsTotalFullyPaid && showInvoice" class="pay-in__checkbox mb-1">
                    <input type="checkbox" id="createInvoicePartial" v-model="form.createInvoice">
                    <label for="createInvoicePartial" class="mb-0 pay-in__checkbox-label">
                        Create invoice after saving
                    </label>
                </div>

                <div class="pay-in__checkbox">
                    <input type="checkbox" id="addBillingDetailsPartial" v-model="form.addBillingDetails">
                    <label for="addBillingDetailsPartial" class="mb-0 pay-in__checkbox-label">
                        Add billing details
                    </label>
                </div>
            </div>

            <billing-details-form
                v-if="form.addBillingDetails"
                :billingDetails="form.billingDetails"
            />

            <!-- Create payment button -->
            <div v-if="!editPayment" class="mt-3 mb-3">
                <button type="button" class="btn green pay-in__create-btn" :disabled="!isItemsTotalFullyPaid || !isBillingValid || !isReferenceValid || hasRedLabels" @click="createPayment">
                    <i v-if="!isEditingPayment" class="ico-add fs-smallest mr-1"></i> {{ isEditingPayment ? 'Update payment' : 'Create payment' }}
                </button>
            </div>
        </template>

        <!-- Due to Pay / Total -->
        <div class="d-flex justify-content-end align-items-center mt-auto pt-2">
            <label class="mr-2">Due to Pay</label>
            <span class="fs-large font-weight-600 mr-4">{{ formatPrice(remainingAmountDue) }} €</span>
            <label class="mr-2">Total</label>
            <span class="fs-large font-weight-600">{{ formatPrice(grandTotal) }} €</span>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { CASH_PAYMENT_TYPE_ID, AGENT_DEPOSIT_PAYMENT_TYPE_ID, buildBillingDetails } from '@/tools/modal/ReservationPayIn/payInHelpers'
import BillingDetailsForm from '@/tools/modal/ReservationPayIn/BillingDetailsForm'
import PaymentMethodForm from '@/tools/modal/ReservationPayIn/PaymentMethodForm'
import CreatedPartialPaymentList from '@/tools/modal/ReservationPayIn/CreatedPartialPaymentList'
import { PricingTypes } from '@/enums/pricingType'

const NOTES_CHARACTER_LIMIT = 50
const REFERENCE_CHARACTER_LIMIT = 50
const CUSTOM_CHANNEL_TYPE = 9999
const WIDGET_MIN_CHANNEL_TYPE = 100

export default {
    components: { BillingDetailsForm, PaymentMethodForm, CreatedPartialPaymentList },
    props: {
        form: {
            type: Object,
            required: true,
        },
        grandTotal: {
            type: Number,
            required: true,
        },
        amountDue: {
            type: Number,
            required: true,
        },
        getReservationCreation: {
            type: Boolean,
            default: false,
        },
        createdPayments: {
            type: Array,
            default: () => [],
        },
        showPaymentDetailsForm: {
            type: Boolean,
            default: true,
        },
        editPayment: {
            type: Object,
            default: null,
        },
        showInvoice: {
            type: Boolean,
            required: false
        }
    },
    data() {
        return {
            NOTES_CHARACTER_LIMIT,
            REFERENCE_CHARACTER_LIMIT,
            paxItems: [],
            extraItems: [],
            paymentFormEntries: [{ type: CASH_PAYMENT_TYPE_ID, amountReceived: null }],
            isEditingPayment: false,
            editItemsBaseline: '',
        }
    },
    created() {
        this.loadItems()
        if (!this.editPayment) return
        const allocations = this.editPayment.allocations || []
        if (allocations.length > 0) {
            this.paymentFormEntries = allocations.map(a => ({ type: a.type, amountReceived: a.amount, id: a.id, change: a.change || 0, canBeDeleted: a.canBeDeleted, isAgentPayment: !!a.isAgentPayment }))
        }
        this.isEditingPayment = true
        this.editItemsBaseline = this.computeItemsSignature()
        this.$nextTick(() => this.$emit('validityChanged', this.isStepValid))
    },
    computed: {
        ...mapGetters(['getSelectedReservation', 'getSalesChannels', 'getPaymentTypeListAll','getReservationCalculations','getReservation']),
        itemsTotal() {
            const paxTotal = this.paxItems.reduce((sum, item) => sum + (item.qtyToAdd * this.getItemPrice(item)), 0)
            const extrasTotal = this.extraItems.reduce((sum, item) => sum + (item.qtyToAdd * this.getItemPrice(item.componentPrice)), 0)
            return paxTotal + extrasTotal
        },
        totalReceived() {
            return this.paymentFormEntries.reduce((sum, payment) => sum + (parseFloat(payment.amountReceived) || 0), 0)
        },
        isItemsTotalFullyPaid() {
            return this.totalReceived === this.itemsTotal && this.itemsTotal > 0;
        },
        createdPaymentsTotal() {
            return this.createdPayments.reduce((sum, p) => sum + (p.total || 0), 0)
        },
        remainingAmountDue() {
            if (this.editPayment) {
                return Math.max(0, this.itemsTotal - this.totalReceived)
            }
            const createdItemsTotal = this.createdPayments.reduce((sum, p) =>
                sum + (p.items || []).reduce((s, item) => s + (item.total || 0), 0), 0)
            const amountAllocated = Math.min(this.totalReceived, this.itemsTotal)
            return Math.max(0, this.amountDue - createdItemsTotal - amountAllocated)
        },
        isBillingValid() {
            if (!this.form.addBillingDetails) return true
            return !!this.form.billingDetails.vat && !!this.form.billingDetails.country
        },
        isReferenceValid() {
            const reference = (this.form.reference || '').trim()
            return reference.length > 0 && reference.length <= REFERENCE_CHARACTER_LIMIT
        },
        hasRedLabels() {
            return this.isItemsTotalFullyPaid && this.paymentFormEntries.some(p => !p.amountReceived)
        },
        isStepValid() {
            if (this.editPayment) {
                const isAmountValid = this.isItemsTotalFullyPaid || this.paymentFormEntries.length === 0
                return isAmountValid && this.isBillingValid && this.isReferenceValid && !this.hasRedLabels
            }
            return this.createdPayments.length > 0 && !this.showPaymentDetailsForm
        },
        itemsSignature() {
            return this.computeItemsSignature()
        },
    },
    watch: {
        isStepValid(val) {
            this.$emit('validityChanged', val)
        },
        itemsSignature(val) {
            if (!this.editPayment) return
            this.$emit('itemsDirtyChanged', val !== this.editItemsBaseline)
        },
        showPaymentDetailsForm(val) {
            this.$emit('paymentDetailsFormView', val)
        },
        createdPayments() {
            this.loadItems()
        },
    },
    methods: {
        getItemPrice(item) {
            if (this.getReservationCreation) {
                return item.discountedGrossprice
            }

            return item.discountedGrossprice !== item.grossprice && item.discountedGrossprice > 0
                ? item.discountedGrossprice
                : item.grossprice
        },
        loadItems() {
            const reservation = this.getReservationCreation || !this.editPayment ? this.getReservationCalculations : this.getSelectedReservation
            const reservationExtras = this.getReservationCreation ? this.getReservation.bookingComponents : this.getSelectedReservation.bookingComponents

            const editPaymentId = this.editPayment ? this.editPayment.id : null
            
            const pricingType = reservation.bookingGenderPrices && reservation.bookingGenderPrices[0]

            const isPerOrder = pricingType && pricingType.genderPriceType === PricingTypes.PerOrder

            const paidPaxQty = {}
            const paidComponentQty = {};

            (reservation.payments || []).forEach(payment => {
                if (editPaymentId && payment.id === editPaymentId) return
                (payment.tickets || []).forEach(ticket => {
                    const key = ticket.itemId || ticket.id
                    paidPaxQty[key] = (paidPaxQty[key] || 0) + ticket.quantity
                });
                (payment.components || []).forEach(component => {
                    const key = component.itemId || component.id
                    paidComponentQty[key] = (paidComponentQty[key] || 0) + component.quantity
                })
            });

            // Quantities consumed by payments created locally in this session (not yet
            // reflected in quantityAvailable). Tracked separately so they can be subtracted
            const localPaxQty = {}
            const localComponentQty = {}
            this.createdPayments.forEach(payment => {
                payment.paxSnapshot.forEach(pax => {
                    if (pax.qtyToAdd > 0) localPaxQty[pax.id] = (localPaxQty[pax.id] || 0) + pax.qtyToAdd
                })
                payment.extrasSnapshot.forEach(extra => {
                    if (extra.qtyToAdd > 0) localComponentQty[extra.id] = (localComponentQty[extra.id] || 0) + extra.qtyToAdd
                })
            })

            const editTicketQty = {}
            const editComponentQty = {}
            if (this.editPayment) {
                ;(this.editPayment.tickets || []).forEach(t => { editTicketQty[t.itemId || t.id] = t.quantity })
                ;(this.editPayment.components || []).forEach(c => { editComponentQty[c.itemId || c.id] = c.quantity })
            }
            this.paxItems = (reservation.bookingGenderPrices || [])
                .map(paxItem => ({
                    ...paxItem,
                    qtyToAdd: this.getReservationCreation ? 0 : editTicketQty[paxItem.id] || 0,
                    minQty: 0,
                    remainingQty: this.getReservationCreation ? paxItem.quantityToBook - (paidPaxQty[paxItem.id] || 0) - (localPaxQty[paxItem.id] || 0) : paxItem.quantityAvailable + (editTicketQty[paxItem.id] || 0) - (localPaxQty[paxItem.id] || 0),
                    sellerName: this.getSellerName(paxItem),
                }))
                .filter(item => {
                    const editQty = editTicketQty[item.id] || 0

                    if (this.editPayment) {
                        const shouldShow = editQty > 0 || item.remainingQty > editQty

                        if (!shouldShow) {
                            return false
                        }
                    } else if (item.remainingQty <= 0) {
                        return false
                    }

                    if (isPerOrder && item.audienceTypeDescription) {
                        return false
                    }

                    return true
                })
                .map(item => ({
                    ...item,
                    audienceTypeDescription: isPerOrder ? `Ticket` : item.audienceTypeDescription,
                }))

            this.extraItems = (reservationExtras || [])
                .map(extra => {
                    extra = { ...extra, componentPrice: extra.componentPrice ? { ...extra.componentPrice } : extra.componentPrice };

                    if (this.getReservationCreation && extra.componentPrice) {
                        extra.id = extra.hash;
                        extra.componentPrice.discountedGrossprice = extra.componentPrice.grossprice;
                    }

                    return {
                        ...extra,
                        qtyToAdd: this.getReservationCreation ? 0 : editComponentQty[extra.id] || 0,
                        minQty: 0,
                        remainingQty: this.getReservationCreation ? extra.quantity - (paidComponentQty[extra.id] || 0) - (localComponentQty[extra.id] || 0) : extra.availableQuantity + (editComponentQty[extra.id] || 0) - (localComponentQty[extra.id] || 0),
                        sellerName: this.getSellerName(extra),
                    };
                })
                .filter(item => {
                    if (!this.editPayment) {
                        return item.remainingQty > 0;
                    }

                    const editQty = editComponentQty[item.id] || 0;

                    return editQty > 0 || item.remainingQty > editQty;
                });
        },
        getSellerName(item) {
            if (item.creatorAgent) return item.creatorAgent.brandName
            if (item.creatorUserAccount && (item.channelType < WIDGET_MIN_CHANNEL_TYPE || item.channelType === CUSTOM_CHANNEL_TYPE)) return item.creatorUserAccount.name
            const channel = this.getSalesChannels.find(salesChannel => salesChannel.channelType === item.channelType)
            return channel ? channel.brandName : ''
        },
        incrementItemQuantity(item) {
            if (item.qtyToAdd < item.remainingQty) item.qtyToAdd++
        },
        decrementItemQuantity(item) {
            const minQty = item.minQty || 0

            if (item.qtyToAdd > minQty) {
                item.qtyToAdd--
            }
        },
        keepSelectedQuantityInBounds(item) {
            const minQty = item.minQty || 0

            if (item.qtyToAdd < minQty) {
                item.qtyToAdd = minQty
            }

            if (item.qtyToAdd > item.remainingQty) {
                item.qtyToAdd = item.remainingQty
            }
        },
        computeItemsSignature() {
            const items = [
                ...this.paxItems.map(item => ({ id: item.id, qty: item.qtyToAdd })),
                ...this.extraItems.map(item => ({ id: item.id, qty: item.qtyToAdd })),
            ]
            const methods = this.paymentFormEntries.map(payment => ({ type: payment.type, amount: payment.amountReceived }))
            return JSON.stringify({ items, methods })
        },
        buildSelectedItemsSummary() {
            const parts = []
            this.paxItems.filter(item => item.qtyToAdd > 0).forEach(item => parts.push(`${item.audienceTypeDescription} x ${item.qtyToAdd}`))
            this.extraItems.filter(item => item.qtyToAdd > 0).forEach(item => parts.push(`${item.component.name} x ${item.qtyToAdd}`))
            return parts.join(', ') || '—'
        },
        createPayment() {
            const paymentMethods = this.paymentFormEntries
                .filter(payment => payment.amountReceived)
                .map(payment => {
                    const matchedPaymentType = this.getPaymentTypeListAll.find(paymentType => paymentType.id === payment.type)
                    return {
                        typeId: payment.type,
                        typeName: matchedPaymentType ? matchedPaymentType.description : '',
                        amountReceived: parseFloat(payment.amountReceived) || 0,
                        id: payment.id || 0,
                        isAgentPayment: payment.type === AGENT_DEPOSIT_PAYMENT_TYPE_ID || !!payment.isAgentPayment,
                    }
                })

            const selectedPaymentItems = [
                ...this.paxItems
                    .filter(item => item.qtyToAdd > 0)
                    .map(item => ({ label: `${item.audienceTypeDescription} x${item.qtyToAdd}`, total: item.qtyToAdd * this.getItemPrice(item) })),
                ...this.extraItems
                    .filter(item => item.qtyToAdd > 0)
                    .map(item => ({ label: `${item.component.name} | ${item.componentPrice.name} x${item.qtyToAdd}`, total: item.qtyToAdd * this.getItemPrice(item.componentPrice) })),
            ]

            const updatedCreatedPayments = [...this.createdPayments, {
                itemsLabel: this.buildSelectedItemsSummary(),
                items: selectedPaymentItems,
                paymentMethods,
                total: this.totalReceived,
                createInvoice: this.form.createInvoice,
                notes: this.form.notes,
                reference: this.form.reference,
                hasBillingDetails: this.form.addBillingDetails,
                billingName: this.form.billingDetails.businessName || '',
                billingDetails: this.form.addBillingDetails ? { ...this.form.billingDetails } : null,
                paxSnapshot: this.paxItems.map(item => ({ ...item })),
                extrasSnapshot: this.extraItems.map(item => ({ ...item })),
                paymentFormEntriesSnapshot: this.paymentFormEntries.map(payment => ({ ...payment })),
            }]
            this.$emit('update:createdPayments', updatedCreatedPayments)

            // Reset payment form
            this.paxItems.forEach(item => { item.qtyToAdd = 0 })
            this.extraItems.forEach(item => { item.qtyToAdd = 0 })
            this.paymentFormEntries = [{ type: CASH_PAYMENT_TYPE_ID, amountReceived: null }]
            this.isEditingPayment = false
            this.form.notes = ''
            this.form.reference = ''
            this.form.createInvoice = false
            this.form.addBillingDetails = false
            this.form.billingDetails = buildBillingDetails()
            this.$emit('update:showPaymentDetailsForm', false)
        },
        removeCreatedPayment(index) {
            const updatedCreatedPayments = this.createdPayments.filter((_, i) => i !== index)
            this.$emit('update:createdPayments', updatedCreatedPayments)
            if (updatedCreatedPayments.length === 0) {
                this.$emit('update:showPaymentDetailsForm', true)
            }
        },
        collectPayload() {
            const paymentMethods = this.paymentFormEntries
                .filter(payment => payment.amountReceived)
                .map(payment => {
                    const matchedPaymentType = this.getPaymentTypeListAll.find(paymentType => paymentType.id === payment.type)
                    return {
                        typeId: payment.type,
                        typeName: matchedPaymentType ? matchedPaymentType.description : '',
                        amountReceived: parseFloat(payment.amountReceived) || 0,
                        id: payment.id || 0,
                        isAgentPayment: payment.type === AGENT_DEPOSIT_PAYMENT_TYPE_ID || !!payment.isAgentPayment,
                    }
                })
            const items = [
                ...this.paxItems.filter(item => item.qtyToAdd > 0).map(item => ({ label: `${item.audienceTypeDescription} x${item.qtyToAdd}`, total: item.qtyToAdd * this.getItemPrice(item) })),
                ...this.extraItems.filter(item => item.qtyToAdd > 0).map(item => ({ label: `${item.component.name} | ${item.componentPrice.name} x${item.qtyToAdd}`, total: item.qtyToAdd * this.getItemPrice(item.componentPrice) })),
            ]
            return {
                items,
                paymentMethods,
                total: this.totalReceived,
                createInvoice: this.form.createInvoice,
                notes: this.form.notes,
                reference: this.form.reference,
                billingDetails: this.form.addBillingDetails ? { ...this.form.billingDetails } : null,
                paxSnapshot: this.paxItems.map(item => ({ ...item })),
                extrasSnapshot: this.extraItems.map(item => ({ ...item })),
            }
        },
        editCreatedPayment(index) {
            const payment = this.createdPayments[index]

            this.paymentFormEntries = payment.paymentFormEntriesSnapshot.map(snapshot => ({ ...snapshot }))
            this.isEditingPayment = true

            const updatedCreatedPayments = this.createdPayments.filter((_, i) => i !== index)
            this.$emit('update:createdPayments', updatedCreatedPayments)
            this.$emit('update:showPaymentDetailsForm', true)

            this.$nextTick(() => {
                payment.paxSnapshot.forEach(snapshot => {
                    const item = this.paxItems.find(p => p.id === snapshot.id)
                    if (item) item.qtyToAdd = snapshot.qtyToAdd
                })
                payment.extrasSnapshot.forEach(snapshot => {
                    const item = this.extraItems.find(e => e.id === snapshot.id)
                    if (item) item.qtyToAdd = snapshot.qtyToAdd
                })
            })
            this.form.addBillingDetails = payment.hasBillingDetails
            if (payment.billingDetails) {
                this.form.billingDetails = { ...payment.billingDetails }
            }
            this.form.notes = payment.notes
            this.form.reference = payment.reference || ''
            this.form.createInvoice = payment.createInvoice
        },
    },
}
</script>

<style lang="scss">
    @import '../../../assets/scss/_variables.scss';

    .pay-in-modal .checkbox-component label {
        font-family: 'Open Sans Regular', Helvetica, Arial, sans-serif !important;
        font-weight: normal !important;
    }

    .pay-in-modal .pay-in__create-btn:not(:disabled):hover,
    .pay-in-modal .pay-in__create-btn:not(:disabled):active {
        background-color: $color-3 !important;
        border-color: rgba($darker, 0.35) !important;
        color: $lightest !important;
        box-shadow: none !important;
    }
</style>

<style lang="scss" scoped>
    @import '../../../assets/scss/_variables.scss';

    $font-size-table: 0.83rem;

    .pay-in__body {
        min-height: 11.25rem;
    }

    .pay-in__separator {
        border: 0;
        border-top: 1px solid $gray-light;
        margin: 0.25rem 0;
    }

    .pay-in__items-table {
        table-layout: fixed;
        font-size: $font-size-table;

        col.col-description { width: 36%; }
        col.col-seller      { width: 20%; }
        col.col-qty         { width: 8%; }
        col.col-price       { width: 12%; }
        col.col-qtyadd      { width: 20%; }
        col.col-total       { width: 12%; }

        td:first-child, th:first-child {
            padding-right: 1rem;
        }
        tr {
            td, th {
                padding-top: 0.25rem!important;
                padding-bottom: 0.25rem!important;
            }
        }
    }

    .pay-in__qty-control {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
    }

    .pay-in__qty-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.1rem;
        height: 1.1rem;
    }

    .pay-in__qty-icon {
        font-size: 0.5rem;
    }

    .pay-in__qty-input {
        width: 1.2rem;
    }

    .pay-in__notes-wrapper {
        display: flex;
        flex-direction: column;
        width: 50%;
    }

    .pay-in__notes-input {
        width: 100%;
    }

    .pay-in__required {
        color: $red;
    }

    .pay-in__notes-counter {
        font-size: 0.75rem;
        color: #999;
        margin-top: 0.25rem;
        margin-left: auto;
        font-weight: 500;
    }
    
    .pay-in__checkbox {
        display: flex;
        align-items: center;
    }

    .pay-in__checkbox input[type='checkbox'] {
        appearance: none !important;
        -webkit-appearance: none !important;

        position: relative !important;

        width: 16px !important;
        height: 16px !important;
        min-width: 16px !important;
        max-width: 16px !important;

        margin: 0 8px 0 0 !important;
        padding: 0 !important;

        border: 1px solid #c9c9c9 !important;
        border-radius: 2px !important;

        background: #fff !important;

        cursor: pointer !important;
        box-sizing: border-box !important;
    }

    .pay-in__checkbox input[type='checkbox']:checked {
        background: $color-1 !important;
        border-color: $color-1 !important;
    }

    .pay-in__checkbox input[type='checkbox']:checked::after {
        content: '' !important;

        position: absolute !important;

        width: 7px !important;
        height: 4px !important;

        left: 3px !important;
        top: 3px !important;

        border-left: 2px solid #fff !important;
        border-bottom: 2px solid #fff !important;

        transform: rotate(-45deg) !important;

        box-sizing: border-box !important;
    }

    .pay-in__checkbox-label {
        font-family: 'Open Sans Regular', Helvetica, Arial, sans-serif !important;
        font-size: 14px !important;
        font-weight: normal !important;
        line-height: 16px !important;

        cursor: pointer !important;
    }
</style>


// WEBPACK FOOTER //
// Step2PartialItems.vue?62b1b5cc